import { HttpService } from "../../modules/shared/services/http.service";
import { 
  MenuLink, 
  MenuLinkCreateDto, 
  MenuLinkUpdateDto,
  MenuLinkResponse
} from "../../modules/shared/model/menu-link.interfaces";

const headers = { "Content-Type": "application/json", "x-custom-lang": "en" } as const;

// Get all menu links with pagination
export const getMenuLinks = async (query?: Record<string, string | number | boolean>): Promise<MenuLinkResponse> => {
  const qs = new URLSearchParams();
  Object.entries(query || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) qs.append(k, String(v));
  });
  const response = await HttpService.get(`api/v1/menu-links${qs.toString() ? `?${qs}` : ""}`, true, { "x-custom-lang": "en" });
  return response;
};

// Get menu links by location (Header=1, Footer=2, Sidebar=3)
export const getMenuLinksByLocation = async (
  locationId: number, 
  query?: Record<string, string | number | boolean>,
  language?: string
): Promise<MenuLinkResponse> => {
  const qs = new URLSearchParams();
  Object.entries(query || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) qs.append(k, String(v));
  });
  const lang = language || "en";
  const response = await HttpService.get(
    `api/v1/menu-links/location/${locationId}${qs.toString() ? `?${qs}` : ""}`, 
    true, 
    { "x-custom-lang": lang }
  );
  return response;
};

// Get header menu links specifically (locationId = 1)
export const getHeaderMenuLinks = async (): Promise<MenuLink[]> => {
  const response = await getMenuLinksByLocation(1, { 
    page: 1, 
    limit: 50,
    sort: JSON.stringify([{ orderBy: "order", order: "asc" }])
  });
  return response.data;
};

// Get footer menu links specifically (locationId = 2)
export const getFooterMenuLinks = async (): Promise<MenuLink[]> => {
  const response = await getMenuLinksByLocation(2, { 
    page: 1, 
    limit: 50,
    sort: JSON.stringify([{ orderBy: "order", order: "asc" }])
  });
  return response.data;
};

// Get sidebar menu links specifically (locationId = 3)
export const getSidebarMenuLinks = async (): Promise<MenuLink[]> => {
  const response = await getMenuLinksByLocation(3, { 
    page: 1, 
    limit: 50,
    sort: JSON.stringify([{ orderBy: "order", order: "asc" }])
  });
  return response.data;
};

// Get menu link by ID
export const getMenuLinkById = async (id: string): Promise<MenuLink> => {
  const response = await HttpService.get(`api/v1/menu-links/${id}`, true, { "x-custom-lang": "en" });
  return response;
};

// Create new menu link
export const createMenuLinkAction = async (data: MenuLinkCreateDto): Promise<MenuLink> => {
  const response = await HttpService.post("api/v1/menu-links", data, headers, true);
  return response;
};

// Update menu link
export const updateMenuLinkAction = async (id: string, data: MenuLinkUpdateDto): Promise<MenuLink> => {
  const response = await HttpService.patch(`api/v1/menu-links/${id}`, data, headers, true);
  return response;
};

// Delete menu link
export const deleteMenuLinkAction = async (id: string): Promise<void> => {
  await HttpService.deleteRecord(`api/v1/menu-links/${id}`, { "x-custom-lang": "en" }, true);
};

// Bulk create menu links
export const bulkCreateMenuLinksAction = async (menuLinks: MenuLinkCreateDto[]): Promise<MenuLink[]> => {
  const response = await HttpService.post("api/v1/menu-links/bulk", { menuLinks }, headers, true);
  return response;
};

// Bulk update menu links
export const bulkUpdateMenuLinksAction = async (menuLinks: Array<{ id: string; data: MenuLinkUpdateDto }>): Promise<MenuLink[]> => {
  const response = await HttpService.patch("api/v1/menu-links/bulk", { menuLinks }, headers, true);
  return response;
};

// Get menu links with advanced filtering
export const getMenuLinksWithFilters = async (filters: {
  locationId?: number;
  visibilityId?: number;
  title?: string;
  url?: string;
}, options?: {
  page?: number;
  limit?: number;
  sort?: Array<{ orderBy: string; order: "asc" | "desc" }>;
}): Promise<MenuLinkResponse> => {
  const query: Record<string, string | number> = {};
  
  // Add filters
  if (filters.locationId) query.locationId = filters.locationId;
  if (filters.visibilityId) query.visibilityId = filters.visibilityId;
  if (filters.title) query.title = filters.title;
  if (filters.url) query.url = filters.url;
  
  // Add pagination options
  if (options?.page) query.page = options.page;
  if (options?.limit) query.limit = options.limit;
  if (options?.sort) query.sort = JSON.stringify(options.sort);
  
  return getMenuLinks(query);
};

// Get menu links by visibility level
export const getMenuLinksByVisibility = async (visibilityId: number, query?: Record<string, string | number | boolean>): Promise<MenuLinkResponse> => {
  return getMenuLinksWithFilters({ visibilityId }, {
    page: query?.page as number || 1,
    limit: query?.limit as number || 50,
    sort: query?.sort ? JSON.parse(query.sort as string) : [{ orderBy: "order", order: "asc" }]
  });
};

// Refresh menu links cache (useful for admin operations)
export const refreshMenuLinksCache = async (): Promise<void> => {
  // This could be implemented to clear any cached menu links
  // For now, it's a placeholder for future cache management
  console.log("Menu links cache refresh requested");
};
